import { useState } from "react";
import moment from "moment";
import {
  TAGS,
  DAYS_SHORT,
  DAILY_HOUR_LIMIT,
  HOLIDAYS_BY_YEAR,
  WEEKENDS,
} from "./constants";
import DayDetails from "./DayDetails";
import TimelogModal from "./TimelogModal";
import { LuPartyPopper, LuUmbrella, LuBan, LuClock } from "react-icons/lu";
import {
  loadTimelogs,
  saveTimelogs,
  addTimelog as serviceAddTimelog,
  updateTimelog as serviceUpdateTimelog,
  deleteTimelog as serviceDeleteTimelog,
  addLeave as serviceAddLeave,
  removeLeave as serviceRemoveLeave,
} from "./services";

const CalendarView = ({ timelogs, setTimelogs }) => {
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [currentDate, setCurrentDate] = useState(moment());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDayDetails, setShowDayDetails] = useState(false);

  // Utility functions
  const getDaysInMonth = (date) => date.daysInMonth();

  const getFirstDayOfMonth = (date) => date.clone().startOf("month").day();

  const generateCalendarDays = () => {
    const year = currentDate.year();
    const month = currentDate.month();
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      const dateStr = moment({ year, month, date: i }).format("YYYY-MM-DD");
      days.push(dateStr);
    }

    return days;
  };

  const getDayLogs = (date) => {
    return timelogs.filter((log) => log.date === date && log.type !== "leave");
  };

  const getTotalHours = (date) => {
    return getDayLogs(date).reduce((sum, log) => sum + log.duration, 0);
  };

  const isHoliday = (date) => {
    const year = moment(date).year();
    const holidays = HOLIDAYS_BY_YEAR[year] || [];
    const holiday = holidays.find((h) => h.date === date);
    return holiday ? holiday : null;
  };

  const isWeekend = (date) => {
    const dayOfWeek = moment(date).day();
    return WEEKENDS.includes(dayOfWeek);
  };

  const isToday = (date) => {
    return moment().format("YYYY-MM-DD") === date;
  };

  const isLeaveDay = (date) => {
    return timelogs.some((log) => log.date === date && log.type === "leave");
  };

  const openEdit = (id) => {
    const log = timelogs.find((l) => l.id === id);
    if (log) {
      setEditData(log);
      setEditingId(id);
      setShowModal(true);
    }
  };

  const addTimelog = (timelog) => {
    serviceAddTimelog(timelog);
    const updated = loadTimelogs();
    setTimelogs(updated);
    setShowModal(false);
    setEditingId(null);
    setEditData(null);
  };

  const updateTimelog = (id, updates) => {
    serviceUpdateTimelog(id, updates);
    const updated = loadTimelogs();
    setTimelogs(updated);
    setShowModal(false);
    setEditingId(null);
    setEditData(null);
  };

  const deleteTimelog = (id) => {
    serviceDeleteTimelog(id);
    const updated = loadTimelogs();
    setTimelogs(updated);
  };

  const addLeave = (date) => {
    serviceAddLeave(date);
    const updated = loadTimelogs();
    setTimelogs(updated);
  };

  const removeLeave = (date) => {
    serviceRemoveLeave(date);
    const updated = loadTimelogs();
    setTimelogs(updated);
  };

  const displayCalendarUI = () => {
    const days = generateCalendarDays();
    return (
      <div className="rounded-lg bg-white p-6 shadow-sm">
        {/* Month Header */}
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-slate-900">
            {currentDate.format("MMMM YYYY")}
          </h2>
          <div className="flex gap-2">
            <button
              onClick={() =>
                setCurrentDate(currentDate.clone().subtract(1, "month"))
              }
              className="rounded-md border border-slate-300 px-3 py-2 hover:bg-slate-50"
            >
              ←
            </button>
            <button
              onClick={() =>
                setCurrentDate(currentDate.clone().add(1, "month"))
              }
              className="rounded-md border border-slate-300 px-3 py-2 hover:bg-slate-50"
            >
              →
            </button>
          </div>
        </div>

        {/* Day Headers */}
        <div className="mb-2 grid grid-cols-7 gap-1">
          {DAYS_SHORT.map((day) => (
            <div
              key={day}
              className="p-2 text-center text-sm font-semibold text-slate-600"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1 auto-rows-max">
          {days.map((date, idx) => {
            if (!date) return <div key={`empty-${idx}`} />;

            const dayLogs = getDayLogs(date);
            const totalHours = getTotalHours(date);
            const holiday = isHoliday(date);
            const weekend = isWeekend(date);
            const today = isToday(date);
            const leave = isLeaveDay(date);
            const isBlocked = holiday || weekend;
            const tasks = [...new Set(dayLogs.map((log) => log.tag))];
            // Day-box styling
            const dayClass = `
            relative min-h-28 rounded-md border p-2 transition-all
            ${today ? "border-blue-500 bg-blue-50 shadow-sm" : ""}
            ${leave ? "border-red-300 bg-red-50" : ""}
            ${holiday ? "border-green-300 bg-green-50" : ""}
            ${
              !holiday && !leave && weekend ? "border-gray-300 bg-gray-100" : ""
            }
            ${
              !today && !holiday && !leave && !weekend
                ? "border-slate-200 bg-white hover:border-blue-300"
                : ""
            }
            ${isBlocked ? "cursor-not-allowed" : "cursor-pointer"}
          `;

            return (
              <div
                key={date}
                className={dayClass}
                onClick={() => {
                  if (!isBlocked) {
                    setSelectedDate(date);
                    setShowDayDetails(true);
                  }
                }}
              >
                {/* DATE */}
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold text-slate-700">
                    {moment(date).date()}
                  </span>

                  <span>
                    <div className="flex flex-col items-end">
                      {holiday ? (
                        <>
                          <LuPartyPopper size={16} className="text-green-600" />
                          <span className="text-[10px] text-green-700 font-medium mt-0.5 leading-tight">
                            {holiday.name}
                          </span>
                        </>
                      ) : null}

                      {leave && (
                        <LuUmbrella size={16} className="text-red-600" />
                      )}
                    </div>
                  </span>
                </div>

                {/* HOURS pill */}
                {totalHours > 0 && !leave && (
                  <div className="absolute top-1.5 right-1.5">
                    <div
                      className="
                      inline-flex items-center gap-1.5 rounded-full text-[11px] font-semibold
                      px-2.5 py-1
                      bg-gradient-to-r from-blue-500 to-blue-600
                      text-white shadow-sm
                    "
                    >
                      <LuClock size={12} />
                      {totalHours}h
                    </div>
                  </div>
                )}

                {dayLogs.length > 0 && !leave && (
                  <div className="flex items-center gap-1 mt-5">
                    {tasks?.map((tagId) => {
                      const tag = TAGS.find((t) => t.id === tagId);
                      return (
                        <span
                          key={tagId}
                          className="w-2 h-2 rounded-full inline-block"
                          style={{ backgroundColor: tag?.color }}
                        ></span>
                      );
                    })}
                  </div>
                )}

                {dayLogs.length > 0 && !leave && (
                  <div className="text-[11px] mt-1 text-slate-600">
                    {dayLogs.length} task{dayLogs.length > 1 ? "s" : ""}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render drawer for desktop
  const renderDesktopDrawer = () => (
    <div className="hidden lg:block">
      <div className="w-full max-w-sm ml-auto rounded-lg bg-white shadow-sm">
        <DayDetails
          date={selectedDate}
          timelogs={getDayLogs(selectedDate)}
          isLeave={isLeaveDay(selectedDate)}
          onClose={() => setShowDayDetails(false)}
          onDelete={deleteTimelog}
          onEdit={openEdit}
          onAddLeave={() => addLeave(selectedDate)}
          onRemoveLeave={() => removeLeave(selectedDate)}
          onAddNew={() => setShowModal(true)}
        />
      </div>
    </div>
  );

  // Render drawer for mobile
  const renderMobileDrawer = () => (
    <div className="fixed inset-0 z-50 flex lg:hidden">
      <div
        className="flex-1 bg-black/30"
        onClick={() => setShowDayDetails(false)}
      />
      <div className="w-80 max-w-full bg-white shadow-xl border-l border-slate-200">
        <DayDetails
          date={selectedDate}
          timelogs={getDayLogs(selectedDate)}
          isLeave={isLeaveDay(selectedDate)}
          onClose={() => setShowDayDetails(false)}
          onDelete={deleteTimelog}
          onEdit={openEdit}
          onAddLeave={() => addLeave(selectedDate)}
          onRemoveLeave={() => removeLeave(selectedDate)}
          onAddNew={() => setShowModal(true)}
        />
      </div>
    </div>
  );

  return (
    <div
      className={`grid gap-6 transition-all duration-300 ${
        showDayDetails
          ? "lg:grid-cols-[minmax(0,2fr)_minmax(0,0.7fr)]"
          : "lg:grid-cols-1"
      }`}
    >
      {displayCalendarUI()}
      {showDayDetails && selectedDate && renderDesktopDrawer()}
      {showDayDetails && selectedDate && renderMobileDrawer()}
      {showModal && (
        <TimelogModal
          date={selectedDate}
          editingId={editingId}
          editData={editData}
          timelogs={timelogs}
          onClose={() => {
            setShowModal(false);
            setEditingId(null);
            setEditData(null);
          }}
          onSave={
            editingId ? (data) => updateTimelog(editingId, data) : addTimelog
          }
        />
      )}
    </div>
  );
};

export default CalendarView;
